import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import cytoscape, { type Core, type ElementDefinition } from 'cytoscape'
import fcose from 'cytoscape-fcose'
import type { AppMode } from '../state'
import type { GraphEdge, GraphNode, Locale, NodeType } from '../data/schema'
import { relationshipColors } from '../graphTokens'
import { localize } from '../i18n'
import { minimumPresetDistance, storyOverviewLayoutTokens } from '../storyOverviewLayout'
import type { RelationshipLabelMode } from './FilterPanel'
import { typeIconDataUri } from './TypeIcon'

cytoscape.use(fcose)

interface GraphCanvasProps {
  nodes: GraphNode[]
  edges: GraphEdge[]
  locale: Locale
  mode: AppMode
  focusId?: string
  labelMode: RelationshipLabelMode
  contextStoryId?: string
  contextStepId?: string
  presetPositions?: Readonly<Record<string, { x: number; y: number }>>
  highlightedType?: NodeType
  onSelectNode: (id: string) => void
  onSelectEdge: (id: string) => void
  onExpandNode: (id: string) => void
}

interface FitVisibleContextOptions {
  storyOverview?: boolean
  minimumPresetDistance?: number
}

export interface GraphCanvasHandle {
  exportPng: () => Promise<Blob>
}

const colors: Record<NodeType, string> = {
  deity: '#D6A647', hero: '#4D8FCC', mortal: '#CDAE79', creature: '#CF6F5C', place: '#4F9A78', event: '#8C69A8', artifact: '#C58A45',
}

const shapes = {
  deity: 'ellipse', hero: 'round-rectangle', mortal: 'round-rectangle', creature: 'octagon', place: 'diamond', event: 'hexagon', artifact: 'barrel',
} as const

export function getNodeFocusContext(cy: Core, focusId: string) {
  const focusNode = cy.getElementById(focusId).filter('node')
  if (focusNode.empty()) return cy.collection()
  const directContext = focusNode.closedNeighborhood()
  const directNodes = directContext.nodes()
  const closureEdges = cy.edges('[relationshipRole = "secondary"]').filter((edge) => (
    directNodes.contains(edge.source()) && directNodes.contains(edge.target())
  ))
  return directContext.union(closureEdges)
}

export function storyOverviewCameraScale(viewportWidth: number) {
  if (viewportWidth <= 760) return 1.85
  if (viewportWidth <= 1180) return 1.62
  return 1.45
}

export function storyOverviewMinimumRenderedNodeSize(viewportWidth: number) {
  if (viewportWidth <= 760) return 50
  if (viewportWidth <= 1180) return 54
  return 64
}

export function storyOverviewMinimumRenderedSpacing() {
  return 96
}

export function storyOverviewMinimumZoom(viewportWidth: number, nodeCount: number, minimumDistance: number) {
  const nodeSizeZoom = storyOverviewMinimumRenderedNodeSize(viewportWidth) / storyOverviewLayoutTokens(nodeCount).nodeSize
  const spacingZoom = minimumDistance > 0 ? storyOverviewMinimumRenderedSpacing() / minimumDistance : 0
  return Math.max(nodeSizeZoom, spacingZoom)
}

export function fitVisibleContext(cy: Core, focusId?: string, options: FitVisibleContextOptions = {}) {
  if (cy.destroyed()) return
  const container = cy.container()
  if (!container || container.clientWidth < 80 || container.clientHeight < 80) return
  const focusNode = focusId ? cy.getElementById(focusId).filter('node') : cy.collection()
  const focusEdge = focusId ? cy.getElementById(focusId).filter('edge') : cy.collection()
  const context = !focusNode.empty()
    ? getNodeFocusContext(cy, focusId!)
    : !focusEdge.empty() ? focusEdge.connectedNodes().union(focusEdge) : cy.elements()
  if (context.empty()) return
  cy.fit(context, window.matchMedia('(max-width: 760px)').matches ? 42 : 70)
  const fittedZoom = cy.zoom()
  const baselineScale = options.storyOverview && !focusId ? storyOverviewCameraScale(window.innerWidth) : 1
  const minimumOverviewZoom = options.storyOverview && !focusId
    ? storyOverviewMinimumZoom(
      window.innerWidth,
      cy.nodes().length,
      options.minimumPresetDistance ?? minimumPresetDistance(Object.fromEntries(cy.nodes().map((node) => [node.id(), node.position()]))),
    )
    : fittedZoom
  const targetZoom = Math.max(fittedZoom * baselineScale, minimumOverviewZoom)
  if (targetZoom > fittedZoom) cy.zoom({
    level: Math.min(cy.maxZoom(), targetZoom),
    renderedPosition: { x: container.clientWidth / 2, y: container.clientHeight / 2 },
  })
  container.dataset.fittedZoom = fittedZoom.toFixed(4)
  container.dataset.cameraZoom = cy.zoom().toFixed(4)
  container.dataset.storyOverviewCameraScale = (cy.zoom() / fittedZoom).toFixed(2)
}

export function shouldAllowNodeDragging(mode: AppMode, focusId: string | undefined, isMobile: boolean) {
  return mode !== 'story' && Boolean(focusId) && !isMobile
}

function seededPosition(id: string, index: number, count: number) {
  const seed = [...id].reduce((sum, character) => sum + character.charCodeAt(0), 0)
  const angle = ((index + (seed % 11) / 11) / Math.max(count, 1)) * Math.PI * 2 - Math.PI / 2
  return { x: 410 + Math.cos(angle) * 300, y: 310 + Math.sin(angle) * 185 }
}

export const GraphCanvas = forwardRef<GraphCanvasHandle, GraphCanvasProps>(function GraphCanvas({ nodes, edges, locale, mode, focusId, labelMode, contextStoryId, contextStepId, presetPositions, highlightedType, onSelectNode, onSelectEdge, onExpandNode }, ref) {
  const containerRef = useRef<HTMLDivElement>(null)
  const cyRef = useRef<Core | null>(null)
  const layoutKeyRef = useRef('')
  const focusRef = useRef(focusId)
  const callbacksRef = useRef({ onSelectNode, onSelectEdge, onExpandNode })
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.matchMedia?.('(max-width: 760px)').matches ?? false)
  const isStoryOverview = mode === 'story' && Boolean(contextStoryId) && !contextStepId && Boolean(presetPositions)
  const storyOverviewRef = useRef(isStoryOverview)
  const nodesDraggable = shouldAllowNodeDragging(mode, focusId, isMobileViewport)
  const focusedNodeId = focusId && nodes.some((node) => node.id === focusId) ? focusId : undefined
  const directNeighborIds = new Set(focusedNodeId
    ? edges.filter((edge) => edge.source === focusedNodeId || edge.target === focusedNodeId)
      .map((edge) => edge.source === focusedNodeId ? edge.target : edge.source)
    : [])
  const relationshipRole = (edge: GraphEdge) => {
    if (!focusedNodeId || edge.source === focusedNodeId || edge.target === focusedNodeId) return 'primary'
    if (directNeighborIds.has(edge.source) && directNeighborIds.has(edge.target)) return 'secondary'
    return 'extended'
  }
  const secondaryEdgeCount = edges.filter((edge) => relationshipRole(edge) === 'secondary').length
  const focusContextNodeCount = focusedNodeId ? directNeighborIds.size + 1 : 0
  const unrelatedNodeCount = focusedNodeId ? Math.max(0, nodes.length - focusContextNodeCount) : 0
  const presetMinimumDistance = presetPositions ? minimumPresetDistance(presetPositions) : undefined
  const presetMinimumDistanceRef = useRef(presetMinimumDistance)
  const presetKey = presetPositions
    ? Object.entries(presetPositions).sort(([left], [right]) => left.localeCompare(right)).map(([id, point]) => `${id}:${point.x}:${point.y}`).join(',')
    : ''
  const topologyKey = `${mode}|${nodes.map((node) => node.id).sort().join(',')}|${edges.map((edge) => `${edge.id}:${edge.source}:${edge.target}`).sort().join(',')}|${presetKey}`
  const layoutKey = mode === 'story'
    ? `${mode}|${nodes.map((node) => node.id).sort().join(',')}|${presetKey}`
    : topologyKey

  focusRef.current = focusId
  storyOverviewRef.current = isStoryOverview
  presetMinimumDistanceRef.current = presetMinimumDistance
  callbacksRef.current = { onSelectNode, onSelectEdge, onExpandNode }

  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 760px)')
    if (!query) return
    const updateViewport = () => setIsMobileViewport(query.matches)
    updateViewport()
    query.addEventListener?.('change', updateViewport)
    return () => query.removeEventListener?.('change', updateViewport)
  }, [])

  useImperativeHandle(ref, () => ({
    async exportPng() {
      const cy = cyRef.current
      const container = containerRef.current
      if (!cy || !container) throw new Error('The graph is not ready to export.')

      await document.fonts?.ready
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
      return cy.png({ output: 'blob-promise', bg: '#08111C', full: true, maxWidth: 1800, maxHeight: 1800 })
    },
  }), [])

  useEffect(() => {
    if (!containerRef.current) return
    const nodeStyles = Object.entries(colors).map(([type, color]) => ({
      selector: `node[type = "${type}"]`,
      style: { 'background-color': color, shape: shapes[type as NodeType] },
    }))
    const edgeStyles = Object.entries(relationshipColors).map(([type, color]) => ({
      selector: `edge[type = "${type}"]`,
      style: { 'line-color': color, 'target-arrow-color': color },
    }))
    const cy = cytoscape({
      container: containerRef.current,
      elements: [],
      minZoom: 0.42,
      maxZoom: 2.3,
      boxSelectionEnabled: false,
      autoungrabify: !nodesDraggable,
      style: [
        {
          selector: 'node',
          style: {
            width: 66, height: 66, label: 'data(label)', color: '#F4F0E8',
            'font-family': 'Noto Sans TC, sans-serif', 'font-size': 10, 'font-weight': 600,
            'text-valign': 'bottom', 'text-margin-y': 10, 'text-outline-color': '#08111C', 'text-outline-width': 3,
            'text-wrap': 'wrap', 'text-max-width': '116px', 'text-justification': 'center', 'line-height': 1.35,
            'border-width': 1.5, 'border-color': '#F2D793', 'background-opacity': 0.95,
            'overlay-opacity': 0,
          },
        },
        ...nodeStyles,
        { selector: 'node[dense = 1]', style: { width: 58, height: 58, 'font-size': 9, 'text-max-width': '104px', 'text-margin-y': 9 } },
        {
          selector: 'edge',
          style: {
            width: 1.25, 'curve-style': 'bezier', opacity: 0.68,
            'target-arrow-shape': (element: cytoscape.EdgeSingular) => element.data('directed') ? 'triangle' : 'none',
            'arrow-scale': 0.75, label: 'data(displayLabel)', color: '#E7DCC8', 'font-size': 9,
            'text-background-color': '#0F1B28', 'text-background-opacity': 0.94, 'text-background-padding': '4px',
            'text-rotation': 'autorotate', 'overlay-opacity': 0,
          },
        },
        ...edgeStyles,
        { selector: 'edge[contextTier = "story"]', style: { opacity: 0.48 } },
        { selector: 'edge[contextTier = "outside"]', style: { opacity: 0.3, 'line-style': 'dashed' } },
        { selector: 'edge[relationshipRole = "secondary"]', style: { width: 1, opacity: 0.34, 'line-style': 'dotted' } },
        { selector: 'edge[relationshipRole = "extended"]', style: { width: 1, opacity: 0.22, 'line-style': 'dotted' } },
        { selector: '[typeHighlight = "context"]', style: { opacity: 0.2, 'text-opacity': 0.16 } },
        { selector: '.hovered, .selected-edge', style: { label: 'data(label)', width: 3, opacity: 1, 'z-index': 10 } },
        { selector: '.selected-node', style: { width: 88, height: 88, opacity: 1, 'text-opacity': 1, 'border-width': 4, 'border-color': '#FFF0B7', 'z-index': 20 } },
        { selector: '.dimmed', style: { opacity: 0.18, 'text-opacity': 0.12 } },
      ] as unknown as cytoscape.StylesheetJson,
    })
    cyRef.current = cy

    let lastTap = { id: '', time: 0 }
    cy.on('tap', 'node', (event) => {
      const id = event.target.id()
      const now = Date.now()
      if (lastTap.id === id && now - lastTap.time < 360) callbacksRef.current.onExpandNode(id)
      else callbacksRef.current.onSelectNode(id)
      lastTap = { id, time: now }
    })
    cy.on('tap', 'edge', (event) => callbacksRef.current.onSelectEdge(event.target.id()))
    cy.on('mouseover', 'edge', (event) => event.target.addClass('hovered'))
    cy.on('mouseout', 'edge', (event) => event.target.removeClass('hovered'))

    let resizeTimer: number | undefined
    const resizeObserver = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(() => {
      if (cy.destroyed()) return
      cy.resize()
      if (resizeTimer) window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(() => fitVisibleContext(cy, focusRef.current, {
        storyOverview: storyOverviewRef.current,
        minimumPresetDistance: presetMinimumDistanceRef.current,
      }), 120)
    })
    resizeObserver?.observe(containerRef.current)

    return () => {
      resizeObserver?.disconnect()
      if (resizeTimer) window.clearTimeout(resizeTimer)
      cy.destroy()
      cyRef.current = null
      layoutKeyRef.current = ''
    }
  }, [])

  useEffect(() => {
    cyRef.current?.autoungrabify(!nodesDraggable)
  }, [nodesDraggable])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    const showLabels = labelMode === 'always' || (labelMode === 'auto' && nodes.length <= 12)
    const nextNodeIds = new Set(nodes.map((node) => node.id))
    const nextEdgeIds = new Set(edges.map((edge) => edge.id))
    const addedNodeIds: string[] = []

    cy.batch(() => {
      cy.edges().filter((item) => !nextEdgeIds.has(item.id())).remove()
      cy.nodes().filter((item) => !nextNodeIds.has(item.id())).remove()

      nodes.forEach((node, index) => {
        const primaryName = localize(node.name, locale)
        const secondaryName = locale === 'en' ? undefined : node.name.en
        const data = {
          id: node.id,
          label: !secondaryName || primaryName === secondaryName ? primaryName : `${primaryName}\n${secondaryName}`,
          type: node.type,
          hasImage: node.media ? 1 : 0,
          image: node.media ? `${import.meta.env.BASE_URL}${node.media.src.replace(/^\//, '')}` : '',
          typeIcon: typeIconDataUri(node.type),
          typeHighlight: !highlightedType || node.type === highlightedType ? 'current' : 'context',
          dense: nodes.length >= 30 ? 1 : 0,
        }
        const existing = cy.getElementById(node.id)
        let graphNode = existing
        if (existing.empty()) {
          const definition: ElementDefinition = { data, position: presetPositions?.[node.id] ?? seededPosition(node.id, index, nodes.length) }
          graphNode = cy.add(definition)
          addedNodeIds.push(node.id)
        } else existing.data(data)
        graphNode.style(node.media ? {
          'background-image': `${data.typeIcon} ${data.image}`,
          'background-fit': 'none cover',
          'background-clip': 'node node',
          'background-image-containment': 'over inside',
          'background-width': '18px 100%',
          'background-height': '18px 100%',
          'background-position-x': '74% 50%',
          'background-position-y': '74% 50%',
        } : {
          'background-image': data.typeIcon,
          'background-fit': 'contain',
          'background-clip': 'node',
          'background-image-containment': 'inside',
          'background-width': '28px',
          'background-height': '28px',
          'background-position-x': '50%',
          'background-position-y': '50%',
        })
      })

      edges.forEach((edge) => {
        const hasStoryContext = Boolean(contextStoryId || contextStepId)
        const inStep = Boolean(contextStepId && edge.contexts.some((context) => context.stepIds.includes(contextStepId)))
        const inStory = Boolean(contextStoryId && (edge.storyRefs.includes(contextStoryId) || edge.contexts.some((context) => context.storyIds.includes(contextStoryId))))
        const data = {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          label: localize(edge.label, locale),
          displayLabel: showLabels ? localize(edge.label, locale) : '',
          type: edge.type,
          directed: edge.directed,
          contextTier: !hasStoryContext || inStep || (!contextStepId && inStory) ? 'current' : inStory ? 'story' : 'outside',
          relationshipRole: relationshipRole(edge),
          typeHighlight: !highlightedType || nodes.some((node) => (node.id === edge.source || node.id === edge.target) && node.type === highlightedType) ? 'current' : 'context',
        }
        const existing = cy.getElementById(edge.id)
        if (existing.empty()) cy.add({ data })
        else existing.data(data)
      })
    })

    if (layoutKeyRef.current === layoutKey) return
    const hadLayout = Boolean(layoutKeyRef.current)
    layoutKeyRef.current = layoutKey
    if (mode === 'story') nodes.forEach((node, index) => cy.getElementById(node.id).position(presetPositions?.[node.id] ?? seededPosition(node.id, index, nodes.length)))
    if (mode !== 'story' && hadLayout) {
      addedNodeIds.forEach((id, index) => cy.getElementById(id).position(seededPosition(id, index, Math.max(addedNodeIds.length, 1))))
    }
    const layout = cy.layout(mode === 'story'
      ? { name: 'preset', fit: false, padding: 80 }
      : { name: 'fcose', animate: false, quality: 'default', randomize: !hadLayout, nodeRepulsion: 7800, idealEdgeLength: 115, nodeSeparation: 90, padding: 70 } as cytoscape.LayoutOptions)
    layout.one('layoutstop', () => window.requestAnimationFrame(() => fitVisibleContext(cy, focusRef.current, {
      storyOverview: storyOverviewRef.current,
      minimumPresetDistance: presetMinimumDistanceRef.current,
    })))
    layout.run()
    if (containerRef.current) containerRef.current.dataset.layoutCount = String(Number(containerRef.current.dataset.layoutCount ?? 0) + 1)
  }, [nodes, edges, locale, mode, labelMode, contextStoryId, contextStepId, topologyKey, focusedNodeId, presetPositions, highlightedType])

  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    cy.elements().removeClass('selected-node selected-edge dimmed')
    if (focusId) {
      const focusNode = cy.getElementById(focusId).filter('node')
      const focusEdge = cy.getElementById(focusId).filter('edge')
      if (!focusNode.empty()) {
        focusNode.addClass('selected-node')
        const context = getNodeFocusContext(cy, focusId)
        cy.elements().not(context).addClass('dimmed')
      } else if (!focusEdge.empty()) {
        focusEdge.addClass('selected-edge')
        const context = focusEdge.connectedNodes().union(focusEdge)
        cy.elements().not(context).addClass('dimmed')
      }
    }
    window.requestAnimationFrame(() => fitVisibleContext(cy, focusId, {
      storyOverview: storyOverviewRef.current,
      minimumPresetDistance: presetMinimumDistanceRef.current,
    }))
  }, [focusId, topologyKey])

  return <div
    ref={containerRef}
    className="graph-canvas"
    role="img"
    aria-label={locale === 'en' ? 'Interactive mythology relationship graph. Use search and the Inspector for a keyboard-readable path.' : '互動神話人物關係圖。可使用搜尋及詳情面板，以鍵盤閱讀完整關係。'}
    data-testid="graph-canvas"
    data-node-count={nodes.length}
    data-edge-count={edges.length}
    data-secondary-edge-count={secondaryEdgeCount}
    data-focus-context-node-count={focusContextNodeCount}
    data-unrelated-node-count={unrelatedNodeCount}
    data-focus-visible={Boolean(focusedNodeId)}
    data-nodes-draggable={nodesDraggable}
    data-type-icons="true"
    data-type-badge-size="18"
    data-position-source={presetPositions ? 'atlas' : 'story'}
    data-minimum-preset-distance={Number.isFinite(presetMinimumDistance) ? presetMinimumDistance!.toFixed(1) : ''}
    data-highlighted-type={highlightedType ?? ''}
  />
})
